import { Router } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useFloating, useHover, useInteractions } from '@floating-ui/react';

// mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import Container from '@mui/material/Container';

// hooks
import useAuth from 'hooks/useAuth';
import useVirgoContent from 'hooks/useVirgoContent';

// utils
import {
  UIEdit,
  UIMenuSection as UIMenuSectionTypes,
  UISandboxMenuSection as UISandboxMenuSectionTypes,
} from '@creoate/virgo-content-api';
import { INavLink } from '../utils/types';

// components
import TopMenuLink from './TopMenuLink';
import UIMenuSection from './UIMenuSection';
import UISandboxMenuSection from './UISandboxMenuSection';

const TopMenu = () => {
  const { topMenu } = useVirgoContent();
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredNavLinkItem, setHoveredNavLinkItem] = useState<INavLink | null>(null);

  const shouldOpenPopover = hoveredNavLinkItem !== null && hoveredNavLinkItem?.component !== 'ui.edit';

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const hover = useHover(context, {
    delay: {
      open: !shouldOpenPopover ? 300 : 0,
      close: 0,
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const handlePopover = (item: INavLink): void => setHoveredNavLinkItem(item);

  const closePopover = useCallback(() => {
    setHoveredNavLinkItem(null);
    setIsOpen(false);
  }, []);

  const paperMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!e?.relatedTarget || !(e?.relatedTarget as HTMLElement)?.className?.includes('top-menu-link')) {
      closePopover();
    }
  };

  // Assign both refs: for floating-ui and anchorEl
  const boxRef = (node: HTMLDivElement | null) => {
    refs.setReference(node); // for floating-ui
    setAnchorEl(node); // for Popover anchoring
  };

  useEffect(() => {
    Router.events.on('routeChangeComplete', closePopover);

    return () => {
      Router.events.off('routeChangeComplete', closePopover);
    };
  }, [closePopover]);

  return (
    <Box sx={{ flexGrow: 1 }} onMouseLeave={closePopover} ref={boxRef} {...getReferenceProps()}>
      <Stack
        sx={{ flexDirection: 'row', justifyContent: 'center', gap: '4px' }}
        data-testid="desktop-top-menu-links-wrapper"
      >
        {topMenu?.map((item: UIEdit | UIMenuSectionTypes | UISandboxMenuSectionTypes, index: number) => {
          if (!isAuthenticated && !item?.availableForAnonymousUser) return null;

          return (
            <TopMenuLink
              index={index}
              menuItem={item}
              handlePopover={handlePopover}
              hoveredNavLinkItem={hoveredNavLinkItem}
              key={`nav-item-${index + 1}-${item?.component}`}
            />
          );
        })}
      </Stack>

      <Popover
        sx={{
          width: '100%',
          zIndex: 10000,
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        disablePortal
        style={floatingStyles}
        ref={refs?.setFloating}
        {...getFloatingProps()}
        open={isOpen && shouldOpenPopover}
        marginThreshold={null}
        TransitionComponent={React.Fragment}
        anchorEl={anchorEl}
        data-testid="top-menu-popover"
        slotProps={{
          paper: {
            sx: {
              p: 0,
              m: 'auto',
              width: '100%',
              zIndex: 10000,
              maxHeight: 462,
              maxWidth: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              '&.MuiPaper-elevation2': {
                top: '0 !important',
              },
            },
            elevation: 2,
            onMouseLeave: paperMouseLeave,
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{ height: '100%', display: 'flex', flex: '1 1 auto', minHeight: '0' }}
          data-testid="top-menu-popover-container"
        >
          {hoveredNavLinkItem?.component === 'ui.menu-section' && (
            <UIMenuSection hoveredNavLinkItem={hoveredNavLinkItem} />
          )}
          {hoveredNavLinkItem?.component === 'ui.sandbox-menu-section' && (
            <UISandboxMenuSection hoveredNavLinkItem={hoveredNavLinkItem} />
          )}
        </Container>
      </Popover>
    </Box>
  );
};

export default TopMenu;
